import { useCallback, useContext } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import DruidWebchatContext, { DruidWebchatContextType } from '../WebChatContext';
import sendFiles from 'botframework-webchat-core/lib/actions/sendFiles';
import { makeThumbnail } from '../utils/pictureUtils';
import { useMergedOptions } from '../selectors/useMergedOptions';

interface FileWithPreview {
    name: string;
    size: number;
    url: string;
    thumbnail?: string;
}

export const useFileDropzone = (enabled: boolean = true) => {
    const [druidWebChatState] = useContext<DruidWebchatContextType>(DruidWebchatContext);
    const { store } = druidWebChatState;
    
    const { styleOptions: {
        uploadThumbnailWidth, 
        uploadThumbnailHeight, 
        uploadThumbnailContentType, 
        uploadThumbnailQuality 
    } = {} } = useMergedOptions();

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (!store || !acceptedFiles.length) {
                return;
            }

            try {
                const filesWithPreviews: FileWithPreview[] = await Promise.all(
                    acceptedFiles.map(async (file) => {
                        const objectUrl = window.URL.createObjectURL(file);

                        let thumbnail: string | undefined;
                        try {
                            const thumbResult = await makeThumbnail(
                                file,
                                uploadThumbnailWidth,
                                uploadThumbnailHeight,
                                uploadThumbnailContentType,
                                uploadThumbnailQuality
                            );
                            thumbnail = thumbResult ? thumbResult.toString() : undefined;
                        } catch (error) {
                            console.warn(`Failed to generate thumbnail for ${file.name}:`, error);
                        }

                        return {
                            name: file.name,
                            size: file.size,
                            url: objectUrl,
                            thumbnail,
                        };
                    })
                );

                store.dispatch(sendFiles(filesWithPreviews));
            } catch (error) {
                console.error('Failed to process dropped files:', error);
            }
        },
        [store, uploadThumbnailWidth, uploadThumbnailHeight, uploadThumbnailContentType, uploadThumbnailQuality]
    );


    const dropzoneOptions: DropzoneOptions = {
        onDrop,
        disabled: !enabled,
        noClick: true,
        noKeyboard: true,
        multiple: true,
    };

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
        useDropzone(dropzoneOptions);

    return {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
    };
};
